import { useState } from 'react';
import Feedback from '@mui/icons-material/Feedback';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
// import QuestionMark from '@mui/icons-material/QuestionMark';
import { Box, Drawer as MuiDrawer, Theme, Toolbar, styled } from '@mui/material';
import { CSSObject } from '@mui/system';
import { useAuth0 } from '@auth0/auth0-react';
import { FeedbackDialog } from 'mui-feedback-dialog';
import KnowledgeBaseVideoDialog from '@/components/common/KnowledgeBaseVideoDialog/KnowledgeBaseVideoDialog';
import AppMenuGroup from '@/components/common/Menu/AppMenuGroup';
import { MenuItemDefinitionProps, MenuItemGroupProps } from '@/components/common/Menu/types';
import { useFeedbackUpload } from '@/components/feedback/hooks/useFeedbackUpload';
import { useKnowledgeBaseVideo } from '@/hooks/useKnowledgeBaseVideo';

interface SideBarProperties {
  menuGroups: MenuItemGroupProps[];
  isOpen: boolean;
  hideSupportMenuGroup?: boolean;
}

const drawerWidth = '20%';
const drawerMinWidth = 290;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  minWidth: drawerMinWidth,
  maxHeight: '100vh',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(5)} )`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(6)} )`,
  },
});

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

const customText = {
  title: 'Your feedback',
  contentText:
    'Thank you for giving feedback, this helps us to improve the product. Please describe any issue as detailed as possible. ' +
    'If it is okay to contact you for more details, please enter your email address as well.',
};

function SideBarMenu({ menuGroups, isOpen, hideSupportMenuGroup }: SideBarProperties) {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const { onSubmit: onFeedbackSubmit } = useFeedbackUpload();
  const { user } = useAuth0();
  const { data: knowledgeBaseVideoUrl } = useKnowledgeBaseVideo();

  function openFeedbackDialog() {
    setDialogVisible(true);
  }

  if (menuGroups == null) return <></>;

  const knowledgeBaseItem: MenuItemDefinitionProps | undefined = knowledgeBaseVideoUrl
    ? {
        displayText: 'Instruction video',
        icon: <HelpCenterIcon />,
        onClick: () => setVideoOpen(true),
      }
    : undefined;

  const defaultSupportMenuGroup: MenuItemGroupProps = {
    title: 'Support',
    showTitle: true,
    showDividerAfter: false,
    // icon: <QuestionMark />,
    items: [
      ...(knowledgeBaseItem ? [knowledgeBaseItem] : []),
      {
        displayText: 'Feedback',
        icon: <Feedback />,
        onClick: () => openFeedbackDialog(),
      },
    ],
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 0 }}>
      <Drawer
        variant="permanent"
        open={isOpen}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <Toolbar variant={'dense'} sx={{ minHeight: 54, maxHeight: 54 }} />
        {menuGroups.map((group) => (
          <AppMenuGroup {...{ ...group, collapsed: !isOpen }} key={group.title} />
        ))}

        {!hideSupportMenuGroup && (
          <Box sx={{ position: 'absolute', bottom: 10, width: '100%' }}>
            {<AppMenuGroup {...{ ...defaultSupportMenuGroup, collapsed: !isOpen }} key={'supportMenu'} />}
            <FeedbackDialog
              text={customText}
              open={dialogVisible}
              onClose={() => setDialogVisible(false)}
              onSubmit={onFeedbackSubmit}
              initialEmail={user?.email}
              useScreencapture={true}
            />
            {knowledgeBaseVideoUrl && (
              <KnowledgeBaseVideoDialog open={videoOpen} videoUrl={knowledgeBaseVideoUrl} onClose={() => setVideoOpen(false)} />
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

export default SideBarMenu;
